import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAssetUrl } from "../api/config";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { FaCheck, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { AddToCalendar, CalendarWhen, ViewDirections } from "../utils/bookingModels";

const doctorName = (doctor) => {
  const n = doctor?.name || "Doctor";
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
};

const formatDateLong = (ymd) => {
  const d = new Date((ymd || "") + "T00:00:00");
  if (Number.isNaN(d.getTime())) return ymd || "";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
};

const BookingConfirmedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { doctor, consultationType, selectedDate, selectedSlot, patient, booking } = location.state || {};

  useEffect(() => {
    if (!doctor || !selectedDate || !selectedSlot) {
      navigate("/consultations", { replace: true });
    }
  }, [doctor, selectedDate, selectedSlot, navigate]);

  

  if (!doctor || !selectedDate || !selectedSlot) return null;

  const address = doctor.address || "";

  return (
    <div className="bg-background min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="bg-surface-lowest rounded-2xl shadow-md border border-surface-container max-w-2xl w-full p-8">
            <div className="w-14 h-14 rounded-full bg-primary-container mx-auto flex items-center justify-center mb-4">
              <FaCheck className="text-white" size={22} />
            </div>
            <h1 className="text-2xl font-bold text-center text-on-primary-fixed-var">Booking Confirmed!</h1>
            <p className="text-center text-on-surface-variant mt-2 mb-6">
              Your appointment has been successfully scheduled. We&apos;ve sent the details to your registered email.
            </p>
            <p className="text-center text-xs text-on-surface-variant mb-4">
              Redirecting to dashboard in 3 seconds…
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="bg-primary-fixed/30 border border-surface-container rounded-xl p-4 flex gap-3">
                <img
                  src={getAssetUrl(doctor.image) || "/logo.png"}
                  alt={doctorName(doctor)}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => { e.target.src = "/logo.png"; }}
                />
                <div>
                  <p className="text-xs uppercase text-on-surface-variant">Specialist</p>
                  <p className="font-bold">{doctorName(doctor)}</p>
                  <p className="text-sm text-on-surface-variant">{doctor.specialization || "General Physician"}</p>
                </div>
              </div>
              <div className="bg-primary-fixed/30 border border-surface-container rounded-xl p-4 flex gap-3">
                <FaCalendarAlt className="text-primary mt-1" />
                <div>
                  <p className="text-xs uppercase text-on-surface-variant">Date & Time</p>
                  <p className="font-bold">{booking?.calendar?.displayDate || formatDateLong(selectedDate)}</p>
                  <p className="text-sm text-on-surface-variant">
                    {booking?.calendar?.displayTime || `${selectedSlot.timeSlot} · ${consultationType === "Online" ? "Video" : "In-person"}`}
                  </p>
                </div>
              </div>
            </div>

            {address && (
              <div className="bg-primary-fixed/30 border border-surface-container rounded-xl p-4 flex gap-3 mb-6">
                <FaMapMarkerAlt className="text-primary mt-1" />
                <div>
                  <p className="text-xs uppercase text-on-surface-variant">Clinic Address</p>
                  {(doctor.clinic_name || doctor.hospital) && (
                    <p className="font-bold">{doctor.clinic_name || doctor.hospital}</p>
                  )}
                  <p className="text-sm text-on-surface-variant">{address}</p>
                </div>
              </div>
            )}

            {booking?.doctorConsultationBookingId && (
              <p className="text-center text-xs text-on-surface-variant mb-4">
                Booking ID: {booking.doctorConsultationBookingId}
              </p>
            )}
            {typeof booking?.meetingLink === "string" && /^https?:\/\//i.test(booking.meetingLink) && (
              <p className="text-center text-sm mb-4">
                <a href={booking.meetingLink} className="text-primary underline" target="_blank" rel="noopener noreferrer">Join Consultation</a>
              </p>
            )}
            {patient?.name && (
              <p className="text-center text-sm text-on-surface-variant mb-4">Patient: {patient.name}</p>
            )}

            <div className="flex flex-col items-center gap-3 mb-6">
              <CalendarWhen calendar={booking?.calendar} />
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <AddToCalendar calendar={booking?.calendar} />
                <ViewDirections url={booking?.locationUrl || doctor.locationUrl} />
              </div>
            </div>

            <div className="border-t border-surface-container pt-4 text-center">
              <button
                onClick={() => navigate("/home")}
                className="text-on-surface-variant hover:text-primary font-medium"
              >
                Return to Dashboard →
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default BookingConfirmedPage;
